export type RiskCategory = "baseline" | "commercial" | "custom";

export type RiskDefinition = {
  category: RiskCategory;
  description: string;
};

export const BASELINE_RISKS: Record<string, RiskDefinition> = {
  harassment: {
    category: "baseline",
    description: "Content that expresses, incites, or promotes harassing language towards any target",
  },
  harassment_threatening: {
    category: "baseline",
    description: "Harassment content that also includes violence or serious harm towards any target",
  },
  hate: {
    category: "baseline",
    description:
      "Content that expresses, incites, or promotes hate based on race, gender, ethnicity, religion, nationality, sexual orientation, disability status, or caste",
  },
  hate_threatening: {
    category: "baseline",
    description: "Hateful content that also includes violence or serious harm towards the targeted group",
  },
  illicit: {
    category: "baseline",
    description:
      "Content that includes instructions or advice that facilitate the planning or execution of illicit acts",
  },
  illicit_violent: {
    category: "baseline",
    description: "Illicit content that also includes violence or serious harm",
  },
  self_harm: {
    category: "baseline",
    description: "Content that promotes, encourages, or depicts acts of self-harm",
  },
  self_harm_instructions: {
    category: "baseline",
    description: "Content that encourages performing acts of self-harm or provides instructions",
  },
  self_harm_intent: {
    category: "baseline",
    description: "Content where the speaker expresses that they are engaging or intend to engage in acts of self-harm",
  },
  sexual: {
    category: "baseline",
    description: "Content meant to arouse sexual excitement or promote sexual services",
  },
  sexual_minors: {
    category: "baseline",
    description: "Sexual content that includes an individual who is under 18 years old",
  },
  violence: {
    category: "baseline",
    description: "Content that depicts death, violence, or physical injury",
  },
  violence_graphic: {
    category: "baseline",
    description: "Content that depicts death, violence, or physical injury in graphic detail",
  },
  stereotyping_ridicule: {
    category: "baseline",
    description: "Stereotyping or ridicule based on race, gender, religion, etc.",
  },
  violence_cruelty: {
    category: "baseline",
    description: "Cruelty or suffering inflicted on people or animals for entertainment.",
  },
  sexual_behaviour: {
    category: "baseline",
    description: "Explicit or inappropriate sexual behavior or innuendo.",
  },
  unethical_behaviour: {
    category: "baseline",
    description:
      "Content that promotes antisocial, dangerous, or self-destructive behavior (e.g., violence, drug abuse, or encouraging harm). Note: excessive sugar intake, unhealthy habits, or overconsumption are not considered unethical.",
  },
  controversial_subjects: {
    category: "baseline",
    description: "Inflammatory or divisive topics (e.g., fake news, political manipulation).",
  },
};

export const COMMERCIAL_RISKS: Record<string, RiskDefinition> = {
  alcohol_tobacco: {
    category: "commercial",
    description: "Content mentions or shows alcohol or tobacco products",
  },
  animal_presence: {
    category: "commercial",
    description: "Content shows real animals or pets",
  },
  challenge_consumption: {
    category: "commercial",
    description: "Content presents product consumption as a challenge or game",
  },
  child_appeal: {
    category: "commercial",
    description: "Content explicitly targets children under 13",
  },
  child_consuming: {
    category: "commercial",
    description: "Content shows a child holding or consuming the product",
  },
  child_spokesperson: {
    category: "commercial",
    description: "Content features a child as the main speaker or character",
  },
  competitor_sentiment: {
    category: "commercial",
    description: "Content references or compares to competitor brands",
  },
  consent_violation: {
    category: "commercial",
    description: "Content features people without their knowledge or consent",
  },
  false_urgency: {
    category: "commercial",
    description: "Content uses false urgency tactics to pressure purchase decisions",
  },
  food_misuse: {
    category: "commercial",
    description: "Content shows disrespectful handling or wasting of food products",
  },
  harmful_product_claims: {
    category: "commercial",
    description: "Content makes false, misleading, or dangerous claims about products",
  },
  logos_present: {
    category: "commercial",
    description: "Content displays competitor or third-party brand logos",
  },
  negative_retailer_sentiment: {
    category: "commercial",
    description: "Content makes negative references or comparisons to retail partners",
  },
  overconsumption: {
    category: "commercial",
    description: "Content depicts excessive consumption of the product",
  },
  unsubstantiated_claims: {
    category: "commercial",
    description: "Content makes health, scientific, or regulatory claims without proper evidence",
  },
  banned_claims: {
    category: "commercial",
    description: "Content makes specifically prohibited claims such as disease treatment or hangover cures",
  },
  expected_overlay_texts: {
    category: "commercial",
    description: "Content is missing required disclosure text overlays",
  },
  expected_disclosure_hashtags: {
    category: "commercial",
    description: "Content is missing required disclosure hashtags",
  },
  banned_keywords: {
    category: "commercial",
    description: "Content uses prohibited words or phrases",
  },
  explicit_lyrics: {
    category: "commercial",
    description:
      "Flag content where song lyrics reference sexual behaviour, unethical conduct, or acts of violence or cruelty",
  },
  ai_content: {
    category: "commercial",
    description: "Flag content that is likely to be AI-generated (e.g., generative-image inconsistencies)",
  },
  unlicensed_music_detection: {
    category: "commercial",
    description: "Detect unlicensed music in content",
  },
  unlicensed_popular_music_detection: {
    category: "commercial",
    description: "Detect unlicensed popular music in content",
  },
};

export const ALL_STANDARD_RISKS = {
  ...BASELINE_RISKS,
  ...COMMERCIAL_RISKS,
};

export function getRiskCategory(riskKey: string): RiskCategory {
  if (BASELINE_RISKS[riskKey]) return "baseline";
  if (COMMERCIAL_RISKS[riskKey]) return "commercial";
  return "custom";
}

export function getStandardRiskDescription(riskKey: string): string | undefined {
  return ALL_STANDARD_RISKS[riskKey]?.description;
}

export function getStandardRiskExplanation(riskKey: string): string | undefined {
  if (riskKey === "Improper audience depiction") {
    return "Content shows the product being used by children, pregnant individuals, or vulnerable groups";
  }
  return undefined;
}

// Extended risk definition with tier and vision for onboarding
export type OnboardingRiskDefinition = {
  tier: number;
  description: string;
  vision: boolean;
  name?: string;
  hardcoded_rules?: string;
};

// Risk categories for the onboarding UI
export const ONBOARDING_RISK_CATEGORIES = {
  "Audience & Appropriateness": [
    "child_appeal",
    "child_participant",
    "child_spokesperson",
    "vulnerable_group",
    "animal_presence",
  ],
  "Claims & Messaging Violations": [
    "unsubstantiated_scientific_or_regulatory_claims",
    "negative_product_claims",
    "medical_claims",
    "urgency_tactics",
  ],
  "Context, Depiction & Environment": [
    "negative_retailer_sentiment",
    "in_store_filming",
    "gifs_or_stickers_present",
    "ai_content",
  ],
  "Improper Use, Safety & Consumption": [
    "challenge_consumption",
    "food_misuse",
    "damaged_product",
    "alcohol_tobacco",
    "ingredient_misrepresentation",
  ],
  "IP Infringement": ["third_party_logo_present", "unlicensed_popular_music_detection"],
} as const;

export type OnboardingRiskCategoryName = keyof typeof ONBOARDING_RISK_CATEGORIES;
